/*
 *
 * Login reducer
 *
 */

import { createReducer } from '@reduxjs/toolkit';
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  checkAuth,
  defaultAction,
  defaultActionSuccess,
  defaultActionError,
  refreshTokenRequest,
  refreshTokenSuccess,
  refreshTokenFailure,
  updateAccessToken,
  User,
} from './actions';
import { logoutError, logoutSuccess } from '../../App/actions';

export interface LoginState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  token: string | null;
  data: any;
}

export const initialState: LoginState = {
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  token: null,
  data: null,
};

const loginReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(loginRequest, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(loginSuccess, (state, action) => {
      state.loading = false;
      state.error = null;
      state.user = action.payload;
      state.isAuthenticated = true;
      state.token = action.payload.token;
    })
    .addCase(loginFailure, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
    })
    .addCase(logout, (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
      state.token = null;
    })
    .addCase(logoutSuccess, (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.data = null;
      state.error = null;
    })
    .addCase(logoutError, (state, action) => {
      state.error = action.payload.error;
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.data = null;
    })
    .addCase(checkAuth, (state) => {
      // This is handled in saga, no state change needed here
    })
    .addCase(refreshTokenRequest, (state) => {
      // Token refresh in progress, don't set loading to avoid UI flicker
      state.error = null;
    })
    .addCase(refreshTokenSuccess, (state, action) => {
      // Update the token in state
      if (state.user) {
        state.user.token = action.payload;
      }
      state.token = action.payload;
      state.error = null;
    })
    .addCase(refreshTokenFailure, (state, action) => {
      // Token refresh failed - logout user
      state.user = null;
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.token = null;
    })
    .addCase(updateAccessToken, (state, action) => {
      // Quick token update without changing other state
      if (state.user) {
        state.user.token = action.payload;
      }
      state.token = action.payload;
    })
    .addCase(defaultAction, (state) => {
      // state.loading = true;
      state.error = null;
    })
    .addCase(defaultActionSuccess, (state, action) => {
      state.loading = false;
      state.data = action.payload.data;
    })
    .addCase(defaultActionError, (state, action) => {
      state.loading = false;
      state.error = action.payload.error;
    });
});

export default loginReducer;
